import { useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import machinaLogoPng from "@/assets/machina-logo.png";
import EnvironmentBadge from "@/components/ui/EnvironmentBadge";
import ProjectLockAccessControl from "@/components/ProjectLockAccessControl";
import { useStore } from "@/contexts/StoreContext";
import { observer } from "mobx-react-lite";
import { Link as RouterLink } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import localForage from "localforage";
import { toastInfo } from "@/utils/toast";
import { AppRoute } from "@/types/routes";
import { initialsFromEmail } from "@/utils/user";
import TransactionNotificationsMenu from "@/components/TransactionNotificationsMenu";
import { clearLastVisitedProject } from "@/services/projectLastVisitedPanel";

type AppHeaderVariant = "home" | "project";

interface HeaderMenuOption<TValue extends string> {
  readonly value: TValue;
  readonly label: string;
  readonly disabled?: boolean;
}

interface AppHeaderBaseProps {
  readonly height: number;
  readonly variant: AppHeaderVariant;
  readonly interactionDisabled?: boolean;
}

interface AppHeaderHomeProps extends AppHeaderBaseProps {
  readonly variant: "home";
  readonly onClickNewProject: () => void;
}

interface AppHeaderProjectProps extends AppHeaderBaseProps {
  readonly variant: "project";
  readonly projectId?: string;
  readonly projectName?: string;
  readonly hasStyleDnaApplied: boolean;
  readonly tokensUsed?: number;
  readonly onProjectAction: (value: string) => void;
  readonly onClickSetStyleDna: () => void;
  readonly onClickShare: () => void;
  /** True when the project's owner-lock currently blocks the *current* user from writing - separate
   * from `interactionDisabled` (a transient "job running" concept). Disables write-only header actions
   * (Rename, Set Style DNA) while leaving navigation/Share/view actions available. */
  readonly readOnly?: boolean;
  /** False until the real owner/lock state has loaded - keeps the lock control from flashing an
   * incorrect "Edit access" state (the default when isOwner/isLocked are both still unset). */
  readonly isProjectDetailsLoaded?: boolean;
  readonly isOwner?: boolean;
  readonly isLocked?: boolean;
  readonly ownerEmail?: string | null;
  readonly onToggleLock?: () => void;
  readonly isLockActionPending?: boolean;
}

export type AppHeaderProps = AppHeaderHomeProps | AppHeaderProjectProps;

const headerProjectButtonSx = {
  color: "text.primary",
  textTransform: "none",
  fontSize: 16,
  maxWidth: 280,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
} as const;

const headerSetStyleSx = {
  height: 32,
  borderColor: "primary.main",
  color: "primary.light",
  textTransform: "uppercase",
  letterSpacing: 0.46,
  fontSize: 13,
  px: 2
} as const;

const headerRightOutlinedButtonSx = {
  height: 32,
  color: "text.secondary",
  textTransform: "uppercase",
  letterSpacing: 0.46,
  fontSize: 13
} as const;

const headerNewProjectSx = {
  height: 32,
  borderColor: "primary.light",
  color: "primary.light",
  textTransform: "uppercase",
  letterSpacing: 0.46,
  fontSize: 13,
  px: 2
} as const;

const AppHeader = observer((props: AppHeaderProps) => {
  const store = useStore();
  const { actionStore, userStore } = store;
  const { oktaAuth } = useOktaAuth();
  const [projectMenuAnchorEl, setProjectMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isProjectMenuOpen = Boolean(projectMenuAnchorEl);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isProfileMenuOpen = Boolean(profileMenuAnchorEl);
  const userEmail = userStore.userInfo?.email ?? "";
  const userInitials = initialsFromEmail(userEmail);
  const interactionDisabled = props.interactionDisabled === true;
  const isProjectVariant = props.variant === "project";
  const isOwner = isProjectVariant && props.isOwner === true;
  const isLocked = isProjectVariant && props.isLocked === true;
  /** Blocks write-only actions for the current user; distinct from `interactionDisabled`. */
  const readOnly = isProjectVariant && props.readOnly === true;
  const writeBlocked = interactionDisabled || readOnly;

  const projectOptions: ReadonlyArray<HeaderMenuOption<string>> = [
    { value: "rename", label: "Rename project", disabled: readOnly },
    { value: "settings", label: "Settings", disabled: readOnly }
  ];

  const handleLogout = async () => {
    // Clear persisted app state first (MobX + React Query persister storage).
    await Promise.allSettled([actionStore.clearStoredData(), localForage.clear()]);
    userStore.clearUserInfo();
    clearLastVisitedProject();

    // Okta sign out last: it reads its own token storage to build the logout redirect,
    // then clears it and navigates through Okta's logout flow.
    await oktaAuth.signOut();
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="default"
      sx={{
        height: props.height,
        justifyContent: "center",
        borderBottomWidth: 1,
        borderBottomStyle: "solid"
      }}
    >
      <Box sx={{ px: 4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            <Box
              component={RouterLink}
              to={AppRoute.Home}
              aria-label="Go to home"
              sx={{ display: "inline-flex", alignItems: "center", textDecoration: "none", cursor: "pointer" }}
            >
              <Box component="img" src={machinaLogoPng} alt="Machina" sx={{ height: 23, width: "auto", display: "block" }} />
            </Box>
            <EnvironmentBadge />
          </Box>
          {props.variant === "project" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                component={RouterLink}
                to={AppRoute.Home}
                aria-label="Back to projects"
                size="small"
                disabled={interactionDisabled}
                sx={{ color: "text.primary" }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <>
                <Button
                  variant="text"
                  size="medium"
                  color="inherit"
                  endIcon={<ExpandMoreIcon />}
                  sx={headerProjectButtonSx}
                  aria-controls={isProjectMenuOpen ? "project-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={isProjectMenuOpen ? "true" : undefined}
                  disabled={interactionDisabled}
                  onClick={(event) => {
                    if (interactionDisabled) {
                      return;
                    }

                    setProjectMenuAnchorEl(event.currentTarget);
                  }}
                >
                  {props.projectName ?? "Project"}
                </Button>
                <Menu
                  id="project-menu"
                  anchorEl={projectMenuAnchorEl}
                  open={isProjectMenuOpen}
                  onClose={() => setProjectMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: {
                        mt: 1,
                        minWidth: 130,
                        borderWidth: 1,
                        borderStyle: "solid"
                      }
                    }
                  }}
                >
                  {projectOptions.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      disabled={opt.disabled === true}
                      onClick={() => {
                        setProjectMenuAnchorEl(null);
                        props.onProjectAction(opt.value);
                      }}
                    >
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))}
                </Menu>
              </>
              {props.hasStyleDnaApplied ? (
                <Link
                  component="button"
                  type="button"
                  underline="always"
                  onClick={(event) => {
                    if (writeBlocked) {
                      event.preventDefault();
                      return;
                    }

                    props.onClickSetStyleDna();
                  }}
                  aria-disabled={writeBlocked}
                  sx={{
                    cursor: writeBlocked ? "default" : "pointer",
                    border: 0,
                    background: "none",
                    font: "inherit",
                    p: 0,
                    m: 0,
                    textAlign: "left",
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    lineHeight: 1.43,
                    letterSpacing: "0.17px",
                    fontWeight: 400,
                    textDecorationColor: (theme) => alpha(theme.palette.text.primary, 0.4),
                    "&:hover": {
                      ...(writeBlocked
                        ? null
                        : {
                            color: "text.primary",
                            textDecorationColor: (theme) => alpha(theme.palette.text.primary, 0.55)
                          })
                    }
                  }}
                >
                  Style DNA applied
                </Link>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<BrushOutlinedIcon />}
                  sx={headerSetStyleSx}
                  disabled={writeBlocked}
                  onClick={props.onClickSetStyleDna}
                >
                  Set Style DNA
                </Button>
              )}
              <TransactionNotificationsMenu projectId={props.projectId} disabled={interactionDisabled} />
            </Box>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {props.variant === "project" ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {(props.tokensUsed ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Tokens used
                </Typography>
              </Box>
              {props.isProjectDetailsLoaded === false ? null : (
                <ProjectLockAccessControl
                  isOwner={isOwner}
                  isLocked={isLocked}
                  ownerEmail={props.ownerEmail ?? null}
                  onToggleLock={() => props.onToggleLock?.()}
                  isLockActionPending={props.isLockActionPending === true}
                  disabled={interactionDisabled}
                />
              )}
              <Button
                variant="outlined"
                size="small"
                sx={headerRightOutlinedButtonSx}
                onClick={props.onClickShare}
                disabled={interactionDisabled}
              >
                Share
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              sx={headerNewProjectSx}
              disabled={interactionDisabled}
              onClick={props.onClickNewProject}
            >
              New Project
            </Button>
          )}
          <Tooltip title={userEmail || "Profile"} arrow>
            <IconButton
              onClick={(event) => setProfileMenuAnchorEl(event.currentTarget)}
              size="small"
              disabled={interactionDisabled}
              aria-controls={isProfileMenuOpen ? "profile-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={isProfileMenuOpen ? "true" : undefined}
              sx={{ p: 0 }}
            >
              <Badge variant="dot" overlap="circular" color="success" anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 600 }}>{userInitials || <PersonIcon fontSize="small" />}</Avatar>
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            id="profile-menu"
            anchorEl={profileMenuAnchorEl}
            open={isProfileMenuOpen}
            onClose={() => setProfileMenuAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  mt: 1,
                  minWidth: 125,
                  borderWidth: 1,
                  borderStyle: "solid"
                }
              }
            }}
          >
            <MenuItem
              onClick={() => {
                setProfileMenuAnchorEl(null);
                toastInfo("TODO: Settings");
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={<Typography variant="inherit">Settings</Typography>} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setProfileMenuAnchorEl(null);
                void handleLogout();
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={<Typography variant="inherit">Logout</Typography>} />
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </AppBar>
  );
});

export default AppHeader;
